import { useMemo, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import _orderBy from 'lodash/orderBy';
import _isEmpty from 'lodash/isEmpty';

// State
import {
  setCheckoutDeliveryTimeType,
  setRestaurantDetailsWithoutTime,
} from '../../../state/modules/order/actions';

// Utils
import { isToday } from '../../../utils/helpers';
import parseISO from '../../../utils/parseISO';

// Config
import config from '../../../config';

export default function useDeliveryTimeSwitcher() {
  const intl = useIntl();
  const dispatch = useDispatch();
  const checkout = useSelector(state => state.order.checkout);

  const {
    deliveryType,
    currentRestaurant,
    deliveryRestaurant,
    pickupRestaurant,
    intervals,
    deliveryTimeType,
    payType,
    deliveryTime: { day },
  } = checkout;

  const isIntimeDisabled = useMemo(
    () =>
      currentRestaurant?.available_timed_orders === false,
    [currentRestaurant, payType],
  );

  // Время доставки: как можно скорее / ко времени
  const wantedTimes = useMemo(() => {
    const isInstantlyEnabled =
      deliveryType === 'pickup'
        ? currentRestaurant?.available_takeaway_asap_orders
        : currentRestaurant?.available_delivery_asap_orders;

    const getAsapLabel = () => {
      if (deliveryType === 'delivery' && deliveryRestaurant?.delivery_time) {
        return `~${deliveryRestaurant.delivery_time} ${intl.formatMessage({ id: 'min' })}`;
      }

      if (deliveryType === 'pickup' && pickupRestaurant?.takeaway_prepare_time) {
        return `~${pickupRestaurant.takeaway_prepare_time} ${intl.formatMessage({ id: 'min' })}`;
      }

      return intl.formatMessage({ id: 'order.deliveryTime.soon' });
    };

    return [
      {
        label: getAsapLabel(),
        value: 'instantly',
        disabled: isInstantlyEnabled === false,
      },
      {
        label: intl.formatMessage({ id: 'order.deliveryTime.time' }),
        value: 'intime',
        disabled: isIntimeDisabled,
      },
    ];
  }, [deliveryType, currentRestaurant, payType]);

  // берем список: где ключ - дата вида 2020-05-21, значение - массив массивов, каждый массив вида ["15:30", "15:50"]
  const daysArray = useMemo(() => {
    return !_isEmpty(intervals)
      ? Object.keys(intervals).map(listDay => ({
          value: listDay,
          label: isToday(listDay)
            ? intl.formatMessage({ id: 'date.today.label' })
            : intl.formatDate(parseISO(listDay), { month: 'long', day: 'numeric' }),
        }))
      : [];
  }, [intervals]);

  const hoursMinutesArray = useMemo(() => {
    return !_isEmpty(intervals) && day
      ? _orderBy(
          intervals[day].map(intvls => ({
            label: `${intvls[0]} - ${intvls[1]}`,
            value: intvls[0],
          })),
          ['value'],
          ['asd'],
        )
      : [];
  }, [intervals, day]);

  // сбросить доставку с "ко времени" к "моментальной", если "ко времени" стала недоступна
  useEffect(() => {
    if (deliveryTimeType === 'intime' && isIntimeDisabled) {
      dispatch(setCheckoutDeliveryTimeType('instantly'));
      dispatch(setRestaurantDetailsWithoutTime());
    }
  }, [deliveryTimeType, isIntimeDisabled]);

  return {
    checkout,
    wantedTimes,
    daysArray,
    hoursMinutesArray,
  };
}
